import React, { useContext, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { CssBaseline, Box, CircularProgress } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useAuth } from "./AuthContext";
import { firestore } from "./firebase";
import { doc, getDoc, onSnapshot, query } from "firebase/firestore";
import { propertyCollection, propertyDoc } from "./utils/firestorePaths";
import { touchActivity } from "./utils/userActivity";

import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import Footer from "./components/Footer";
import PropertyProvider from "./providers/PropertyProvider";
import PropertyContext from "./contexts/PropertyContext";
import SelectProperty from "./components/SelectProperty";
import { getRoutesForRole } from "./config/roleConfig";
import { publicRoutes } from "./config/publicRoutes";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsAndConditions from "./components/TermsAndConditions";
import IncomingCallListener from "./components/IncomingCallListener";

const App = () => {
  const { currentUser, loading } = useAuth();
  const [themeMode, setThemeMode] = useState("dark");

  const toggleTheme = () => {
    setThemeMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeMode,
        },
      }),
    [themeMode],
  );

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <CssBaseline />
        <Box
          sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
        >
          {!currentUser && (
            <Navbar
              isLoggedIn={false}
              themeMode={themeMode}
              toggleTheme={toggleTheme}
            />
          )}
          <Box display="flex" sx={{ flexGrow: 1 }}>
            {currentUser ? (
              <React.Fragment>
                <PropertyProvider>
                  <PropertyContent
                    user={currentUser.uid}
                    themeMode={themeMode}
                    toggleTheme={toggleTheme}
                  />
                </PropertyProvider>
              </React.Fragment>
            ) : (
              <Box
                component="main"
                sx={{ flexGrow: 1, p: 3, paddingTop: 9, minWidth: 0 }}
              >
                <Routes>
                  {publicRoutes.map((route) => (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={
                        route.path === "/" || route.path === "/login" ? (
                          <route.component
                            setIsLoggedIn={() => {}}
                            setUserID={() => {}}
                          />
                        ) : (
                          <route.component />
                        )
                      }
                    />
                  ))}
                  <Route path="*" element={<Navigate to="/login" />} />
                </Routes>
              </Box>
            )}
          </Box>
          <Footer />
        </Box>
      </Router>
    </ThemeProvider>
  );
};

const PropertyContent = ({ user, themeMode, toggleTheme }) => {
  const { selectedProperty, selectedClient, setSelectedProperty } =
    useContext(PropertyContext);
  const [userInfo, setUserInfo] = useState(null);
  const [propertyMessages, setPropertyMessages] = useState([]);
  const [propertyStaff, setPropertyStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  //user info
  useEffect(() => {
    const userProfileRef = doc(firestore, "users", user);
    // Load the profile with a listener rather than a one-shot getDoc. On a fresh
    // page load the SDK has never cached this doc, so a concurrent merge write to
    // it — recordLogin() on sign-in, touchActivity() on mount — is built against
    // an "unknown base" and its optimistic snapshot momentarily contains ONLY the
    // just-written field(s) (e.g. just lastActiveAt), making the profile look
    // empty (no userType/properties). We therefore ignore any snapshot with local
    // writes still pending and act on the first server-confirmed one, which
    // always carries the full document.
    let handled = false;

    const applyProfile = async (userDoc) => {
      if (!userDoc.exists()) {
        // No profile doc — nothing to select; stop the spinner.
        setLoading(false);
        return;
      }
      const userDocData = userDoc.data();
      userDocData.id = user;

      // Superuser: portfolio view scoped to the one client they own. Unlike
      // facilities, a superuser has a definite owning clientId, so we resolve
      // selectedClient here — the dashboard reads Clients/{clientId}/properties.
      if (userDocData.userType === "superuser" && userDocData.clientId) {
        setSelectedProperty("all-properties", userDocData.clientId);
        userDocData.activeProperty = {
          name: "Portfolio View",
          clientId: userDocData.clientId,
        };
        setUserInfo(userDocData);
        setLoading(false);
        return;
      }

      if (userDocData.properties) {
        // For facilities users, skip property selection and allow access to all properties
        if (userDocData.userType === "facilities") {
          setSelectedProperty("all-properties"); // Special value for facilities
          userDocData.activeProperty = { name: "All Facilities Properties" };
        } else if (userDocData.properties.length === 1) {
          const onlyProperty = userDocData.properties[0];
          // Fall back to the user doc's own clientId when the property entry
          // predates the properties-under-clients migration backfill.
          const clientId = onlyProperty.clientId || userDocData.clientId;
          setSelectedProperty(onlyProperty.id, clientId);
          //add property info to user data
          const currentPropertyRef = propertyDoc(clientId, onlyProperty.id);
          const currentPropertyDoc = await getDoc(currentPropertyRef);
          if (currentPropertyDoc.exists()) {
            userDocData.activeProperty = {
              ...currentPropertyDoc.data(),
              id: onlyProperty.id,
              clientId,
            };
          } else {
            console.error("COULD NOT FIND PROPERTY");
          }
        } else {
          setProperties(userDocData.properties);
        }
      }
      setUserInfo(userDocData);
      setLoading(false);
    };

    const unsubscribe = onSnapshot(
      userProfileRef,
      { includeMetadataChanges: true },
      (userDoc) => {
        // Skip optimistic, not-yet-acknowledged snapshots (see note above).
        if (userDoc.metadata.hasPendingWrites) return;
        if (handled) return;
        handled = true;
        applyProfile(userDoc);
      },
      (error) => {
        console.error("Error loading user profile: ", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Keep this user's lastActiveAt fresh while the app is open (throttled).
  useEffect(() => {
    if (!user) return;
    touchActivity(user);
    const interval = setInterval(() => touchActivity(user), 5 * 60 * 1000);
    const onFocus = () => touchActivity(user);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [user]);

  // Reconcile selectedClient when the property came from localStorage (a
  // returning user, or a multi-property user picking up a persisted selection):
  // getUser only sets the client on the single-property / dropdown paths, so
  // derive it from the user doc's properties[] entry when it's still missing.
  useEffect(() => {
    if (
      !userInfo ||
      !selectedProperty ||
      selectedProperty === "all-properties"
    ) {
      return;
    }
    if (selectedClient) return;
    const entry = (userInfo.properties || []).find(
      (p) => p.id === selectedProperty,
    );
    // Fall back to the user doc's own clientId when the property entry predates
    // the properties-under-clients migration backfill.
    const clientId = entry?.clientId || userInfo.clientId;
    if (clientId) {
      setSelectedProperty(selectedProperty, clientId);
    }
  }, [userInfo, selectedProperty, selectedClient]);

  useEffect(() => {
    if (selectedProperty !== null) {
      // For facilities users with access to all properties, skip individual property subscriptions
      if (selectedProperty === "all-properties" || !selectedClient) {
        setPropertyStaff([]);
        setPropertyMessages([]);
        return;
      }

      //get staff
      const staffRef = propertyCollection(
        selectedClient,
        selectedProperty,
        "Staff",
      );
      const staffQuerry = query(staffRef);

      const unsubscribeStaff = onSnapshot(staffQuerry, (snapshot) => {
        const staffArray = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPropertyStaff(staffArray);
      });

      //get messages
      const messagesRef = propertyCollection(
        selectedClient,
        selectedProperty,
        "messages",
      );
      const messagesQuerry = query(messagesRef);

      const unsubscribeMessages = onSnapshot(messagesQuerry, (snapshot) => {
        const messagesArray = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((message) => message.isRead === false);
        setPropertyMessages(messagesArray);
      });

      return () => {
        unsubscribeMessages();
        unsubscribeStaff();
      };
    }
  }, [selectedProperty, selectedClient]);

  const multiPropertyUser =
    userInfo?.userType !== "facilities" &&
    userInfo?.userType !== "superuser" &&
    (userInfo?.properties?.length ?? 0) > 1;

  return (
    <React.Fragment>
      <Navbar
        isLoggedIn={true}
        userProperties={multiPropertyUser ? userInfo.properties : null}
        themeMode={themeMode}
        toggleTheme={toggleTheme}
      />
      {loading ? (
        <React.Fragment>
          <CircularProgress />
        </React.Fragment>
      ) : selectedProperty &&
        selectedProperty !== "all-properties" &&
        !selectedClient ? (
        // A property is selected but its owning client hasn't resolved yet.
        // Render a spinner rather than mounting property-scoped routes with a
        // missing clientId (which would throw in the firestorePaths helpers).
        // If this never resolves, the user doc's properties[] entry is missing
        // `clientId` — run the migration backfill (scripts/migrateProperties.js).
        <React.Fragment>
          <CircularProgress />
        </React.Fragment>
      ) : (
        <React.Fragment>
          {selectedProperty ? (
            <React.Fragment>
              <Sidebar user={userInfo} messages={propertyMessages} />
              <Box
                component="main"
                sx={{ flexGrow: 1, p: 3, paddingTop: 9, minWidth: 0 }}
              >
                <Routes>
                  {getRoutesForRole(userInfo.userType).map((route) => (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={
                        <route.component
                          userInfo={userInfo}
                          user={userInfo}
                          property={selectedProperty}
                          selectedProperty={selectedProperty}
                          clientId={selectedClient}
                          selectedClient={selectedClient}
                          messages={propertyMessages}
                          staff={propertyStaff}
                          userId={user}
                        />
                      }
                    />
                  ))}
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsAndConditions />} />
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </Box>
              <IncomingCallListener userInfo={userInfo} />
            </React.Fragment>
          ) : (
            <React.Fragment>
              {/* add page to have user select the active property on login if multiple properties are on account */}
              <Box
                component="main"
                sx={{ flexGrow: 1, p: 3, paddingTop: 9, minWidth: 0 }}
              >
                <SelectProperty
                  properties={properties}
                  fallbackClientId={userInfo?.clientId}
                />
              </Box>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default App;
